import React, { useEffect, useMemo, useState } from "react";
import "./Schedule2.css";

// =========================================
// TIME HELPERS
// =========================================

const getTimeParts = (timeString) => {
  return String(timeString || "")
    .split(/\s+[–—-]\s+/)
    .map((part) => part.trim());
};

const getStartTime = (timeString) => {
  return getTimeParts(timeString)[0] || "";
};

const getEndTime = (timeString) => {
  const parts = getTimeParts(timeString);
  return parts[parts.length - 1] || "";
};

const getSessionTime = (items) => {
  if (!items || items.length === 0) {
    return "";
  }

  const firstItem = items[0];
  const lastItem = items[items.length - 1];

  const startTime = getStartTime(firstItem.time);
  const endTime = getEndTime(lastItem.time);

  if (startTime === endTime) {
    return startTime;
  }

  return `${startTime} – ${endTime}`;
};

// =========================================
// GROUP SCHEDULE
// =========================================

const groupSchedule = (items) => {
  const groups = [];

  items.forEach((item, index) => {
    // Hide internal items
    if (item.isPublic === false) {
      return;
    }

    // Breaks are separate groups
    if (item.type === "break") {
      groups.push({
        id: `break-${index}`,
        type: "break",
        title: item.title,
        time: item.time,
      });

      return;
    }

    const sessionNumber = item.session;

    // Items without a session number are shown individually
    if (sessionNumber === null || sessionNumber === undefined) {
      groups.push({
        id: `item-${index}`,
        type: "single",
        item,
      });

      return;
    }

    // Find the existing session group
    let sessionGroup = groups.find(
      (group) =>
        group.type === "session" &&
        group.sessionNumber === sessionNumber
    );

    if (!sessionGroup) {
      sessionGroup = {
        id: `session-${sessionNumber}`,
        type: "session",
        sessionNumber,
        title:
          item.sessionTitle ||
          `Session ${sessionNumber}`,
        items: [],
      };

      groups.push(sessionGroup);
    }

    sessionGroup.items.push(item);
  });

  return groups;
};

// =========================================
// MAIN COMPONENT
// =========================================

const Schedule2 = () => {
  const [scheduleItems, setScheduleItems] = useState([]);
  const [expandedSessions, setExpandedSessions] = useState({});

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // =========================================
  // LOAD JSON
  // =========================================

  useEffect(() => {
    const controller = new AbortController();

    fetch("/data/schedule2.json", {
      signal: controller.signal,
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error(`Failed to load schedule: ${res.status}`);
        }

        return res.json();
      })
      .then((data) => {
        if (!Array.isArray(data)) {
          throw new Error("Schedule data must be an array.");
        }

        setScheduleItems(data);
        setError("");
      })
      .catch((err) => {
        if (err.name === "AbortError") {
          return;
        }

        console.error("Failed to load schedule2.json", err);
        setError("Unable to load the event schedule.");
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      });

    return () => controller.abort();
  }, []);

  // =========================================
  // GROUPS
  // =========================================

  const scheduleGroups = useMemo(() => {
    return groupSchedule(scheduleItems);
  }, [scheduleItems]);

  const sessionGroups = useMemo(() => {
    return scheduleGroups.filter(
      (group) => group.type === "session"
    );
  }, [scheduleGroups]);

  // =========================================
  // EXPAND / COLLAPSE
  // =========================================

  const toggleSession = (sessionId) => {
    setExpandedSessions((prev) => ({
      ...prev,
      [sessionId]: !prev[sessionId],
    }));
  };

  const expandAllSessions = () => {
    const expanded = {};

    sessionGroups.forEach((group) => {
      expanded[group.id] = true;
    });

    setExpandedSessions(expanded);
  };

  const collapseAllSessions = () => {
    setExpandedSessions({});
  };

  const allSessionsExpanded =
    sessionGroups.length > 0 &&
    sessionGroups.every(
      (group) => expandedSessions[group.id]
    );

  // =========================================
  // RENDER
  // =========================================

  return (
    <section
      id="schedule2"
      className="schedule2-container"
    >
      <div className="container">

        {/* =====================================
            HEADER
        ===================================== */}

        <div className="schedule2-header">

          <h2 className="schedule2-title">
            TEDxSetapak 2026 Event Schedule
          </h2>

          <p className="schedule2-theme">
            T.U.M.B.U.H – Change Worth Growing
          </p>

<p className="schedule2-date">
  31 October 2026 | 9:00 AM – 5:00 PM
</p>

{/* Download Event Agenda */}
<div className="schedule2-download-container">
  <a
    href="/files/TED%20x%20Setapak%20TUMBUH%20AGENDA.pdf"
    download="TED x Setapak TUMBUH AGENDA.pdf"
    className="schedule2-download-btn"
  >
    <i className="fas fa-file-pdf" aria-hidden="true"></i>

    <span>Download Event Agenda</span>

    <i className="fas fa-download" aria-hidden="true"></i>
  </a>

  <p className="schedule2-download-hint">
    Get the full event programme in PDF format
  </p>
</div>

        </div>

        {/* =====================================
            LOADING
        ===================================== */}

        {loading && (
          <p className="schedule2-loading">
            Loading schedule...
          </p>
        )}

        {/* =====================================
            ERROR
        ===================================== */}

        {!loading && error && (
          <p className="schedule2-error">
            {error}
          </p>
        )}

        {/* =====================================
            SCHEDULE
        ===================================== */}

        {!loading && !error && (
          <div className="schedule2-wrapper">

            {/* Expand / Collapse All */}
            {sessionGroups.length > 0 && (
              <div className="schedule2-toolbar">

                <span className="schedule2-toolbar-label">
                  Event Programme
                </span>

                <button
                  type="button"
                  className="schedule2-expand-button"
                  onClick={
                    allSessionsExpanded
                      ? collapseAllSessions
                      : expandAllSessions
                  }
                >
                  {allSessionsExpanded
                    ? "Collapse All"
                    : "Expand All"}
                </button>

              </div>
            )}

            {/* =====================================
                GROUPED PROGRAMME
            ===================================== */}

            {scheduleGroups.length === 0 ? (
              <p className="schedule2-empty">
                No schedule available.
              </p>
            ) : (
              <div className="schedule2-groups">

                {scheduleGroups.map((group) => {

                  // =================================
                  // BREAK
                  // =================================

                  if (group.type === "break") {
                    const isLunch =
                      group.title
                        .toLowerCase()
                        .includes("lunch");

                    return (
                      <div
                        key={group.id}
                        className="schedule2-break"
                      >

                        <div className="schedule2-break-icon">
                          <i
                            className={
                              isLunch
                                ? "fas fa-utensils"
                                : "fas fa-coffee"
                            }
                            aria-hidden="true"
                          />
                        </div>

                        <div className="schedule2-break-content">

                          <span className="schedule2-break-label">
                            {isLunch
                              ? "LUNCH"
                              : "BREAK"}
                          </span>

                          <h4 className="schedule2-break-title">
                            {group.title}
                          </h4>

                          <p className="schedule2-break-time">
                            {group.time}
                          </p>

                        </div>

                      </div>
                    );
                  }

                  // =================================
                  // SINGLE ITEM
                  // =================================

                  if (group.type === "single") {
                    return (
                      <div
                        key={group.id}
                        className="schedule2-single-item"
                      >

                        <span className="schedule2-time">
                          {group.item.time}
                        </span>

                        <span className="schedule2-single-title">
                          {group.item.title}
                        </span>

                      </div>
                    );
                  }

                  // =================================
                  // SESSION
                  // =================================

                  const isExpanded = Boolean(
                    expandedSessions[group.id]
                  );

                  return (
                    <div
                      key={group.id}
                      className={`schedule2-session ${
                        isExpanded ? "expanded" : ""
                      }`}
                    >

                      {/* Session Header */}
                      <button
                        type="button"
                        className="schedule2-session-header"
                        onClick={() =>
                          toggleSession(group.id)
                        }
                        aria-expanded={isExpanded}
                        aria-controls={`${group.id}-content`}
                      >

                        <div className="schedule2-session-info">

                          <span className="schedule2-session-label">
                            SESSION {group.sessionNumber}
                          </span>

                          <h4 className="schedule2-session-title">
                            {group.title}
                          </h4>

<p className="schedule2-session-time">
  {getSessionTime(group.items)}
</p>

{/* Speaker Names Preview */}
{!isExpanded && (
  <div className="schedule2-speaker-preview">
    <div className="schedule2-speaker-preview-names">
      {group.items
        .filter((item) => item.type === "speaker")
        .map((speaker) => speaker.title)
        .join(" • ")}
    </div>

  </div>
)}

                        </div>

                        <div className="schedule2-session-right">

                          <span className="schedule2-session-count">
                            {group.items.length} items
                          </span>

                          <i
                            className={`fas fa-chevron-down schedule2-chevron ${
                              isExpanded ? "rotated" : ""
                            }`}
                            aria-hidden="true"
                          />

                        </div>

                      </button>

                      {/* Session Content */}
                      {isExpanded && (
                        <div
                          id={`${group.id}-content`}
                          className="schedule2-session-content"
                        >

                          {group.items.map((item, index) => (
                            <div
                              key={`${item.time}-${index}`}
                              className={`schedule2-item ${
                                item.type === "speaker"
                                  ? "schedule2-speaker-item"
                                  : ""
                              }`}
                            >

                              <div className="schedule2-time">
                                {item.time}
                              </div>

                              <div className="schedule2-content">

                                <h5 className="schedule2-item-title">
                                  {item.title}
                                </h5>

                              </div>

                            </div>
                          ))}

                        </div>
                      )}

                    </div>
                  );
                })}

              </div>
            )}

            {/* Footer Note */}
            <p className="schedule2-note">
              Programme is subject to change.
            </p>

          </div>
        )}

      </div>
    </section>
  );
};

export default Schedule2;